// Sugalarm — theme-pack ownership context (cycle 174, themes step 4).
//
// One source of truth for "which packs does this account own", so the
// background render (effective-preset fallback), the applyAppearance
// data-bg, and the Settings picker all agree — and a purchase/restore/
// admin-revoke updates them together on the next refresh. Ownership is
// fetched from the server-authoritative GET /api/theme-packs (the client
// never decides ownership). Mounted once in the authenticated shell.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import * as themePacksApi from "../api/themePacks";
import type { ImagePreset, ThemePack } from "../api/themePacks";

export interface PurchaseResult {
  ok: boolean;
  /** The dev simulate-purchase path is disabled (prod) → show "Coming soon". */
  comingSoon?: boolean;
  error?: string;
}

interface ThemePacksState {
  packs: ThemePack[];
  ownedPackIds: ReadonlySet<string>;
  loaded: boolean;
  refresh: () => Promise<void>;
  /** The single purchase seam (dev simulate-purchase this cycle). */
  purchase: (packId: string) => Promise<PurchaseResult>;
  /** Cycle 5: resolved srcs for an image preset, or undefined for a
   *  token/gradient preset or when no CDN base is configured (→ the
   *  MobileBackground layer falls back to aurora). */
  imageSrcOf: (presetId: string) => ImagePreset | undefined;
  /** Cycle 189: the pack a preset belongs to, resolved DYNAMICALLY from the
   *  loaded catalog (so a runtime-minted image preset's owning pack is known
   *  for the effectiveBg ownership fallback). undefined when the preset is in
   *  no loaded pack — the caller then uses the static code mapping. */
  packOf: (presetId: string) => string | undefined;
  /** Cycle 192: every preset id OFFERED to this account in the loaded
   *  catalog (core's hidden-subtracted built-ins + every visible pack's
   *  presets). `effectiveBg` uses it (once `loaded`) to drop a no-longer-
   *  offered selection — a Core-hidden built-in or a deleted upload — to
   *  aurora, leaving the stored value intact. */
  offeredPresetIds: ReadonlySet<string>;
}

const CORE_ONLY: ReadonlySet<string> = new Set(["core"]);

const Ctx = createContext<ThemePacksState>({
  packs: [],
  ownedPackIds: CORE_ONLY,
  loaded: false,
  refresh: async () => {},
  purchase: async () => ({ ok: false }),
  imageSrcOf: () => undefined,
  packOf: () => undefined,
  offeredPresetIds: new Set<string>(),
});

export function ThemePacksProvider({ children }: { children: ReactNode }) {
  const [packs, setPacks] = useState<ThemePack[]>([]);
  const [loaded, setLoaded] = useState(false);

  const ownedPackIds = useMemo<ReadonlySet<string>>(() => {
    const s = new Set<string>(["core"]);
    for (const p of packs) if (p.owned) s.add(p.pack_id);
    return s;
  }, [packs]);

  // Cycle 5: flatten every pack's image presets into one id→srcs map (the
  // single source the MobileBackground layer + the picker resolve from).
  const imageSrcs = useMemo<ReadonlyMap<string, ImagePreset>>(() => {
    const m = new Map<string, ImagePreset>();
    for (const p of packs)
      for (const ip of p.image_presets ?? []) m.set(ip.id, ip);
    return m;
  }, [packs]);

  const imageSrcOf = useCallback(
    (presetId: string): ImagePreset | undefined => imageSrcs.get(presetId),
    [imageSrcs],
  );

  // Cycle 189: presetId → owning pack_id, from the loaded catalog. Lets the
  // render fallback know a runtime-minted image preset's pack (so a revoked
  // entitlement falls back to aurora exactly like the static relics presets).
  const presetToPack = useMemo<ReadonlyMap<string, string>>(() => {
    const m = new Map<string, string>();
    for (const p of packs)
      for (const pid of p.preset_ids) if (!m.has(pid)) m.set(pid, p.pack_id);
    return m;
  }, [packs]);

  const packOf = useCallback(
    (presetId: string): string | undefined => presetToPack.get(presetId),
    [presetToPack],
  );

  // Cycle 192: every preset id currently offered to this account (core's
  // hidden-subtracted built-ins + every visible pack's presets). The render
  // fallback uses it to drop a no-longer-offered stored selection to aurora.
  const offeredPresetIds = useMemo<ReadonlySet<string>>(() => {
    const s = new Set<string>();
    for (const p of packs) for (const pid of p.preset_ids) s.add(pid);
    return s;
  }, [packs]);

  const refresh = useCallback(async () => {
    const r = await themePacksApi.listPacks();
    if (r.ok) {
      setPacks(r.data.packs);
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const purchase = useCallback(
    async (packId: string): Promise<PurchaseResult> => {
      const r = await themePacksApi.purchasePack(packId);
      if (r.ok) {
        await refresh();
        return { ok: true };
      }
      // 404 = the purchase path is unavailable (prod, dev flag off) →
      // "Coming soon" until step 5 wires a real payment rail.
      if (r.error.status === 404) return { ok: false, comingSoon: true };
      return { ok: false, error: r.error.message };
    },
    [refresh],
  );

  const value = useMemo<ThemePacksState>(
    () => ({
      packs,
      ownedPackIds,
      loaded,
      refresh,
      purchase,
      imageSrcOf,
      packOf,
      offeredPresetIds,
    }),
    [
      packs,
      ownedPackIds,
      loaded,
      refresh,
      purchase,
      imageSrcOf,
      packOf,
      offeredPresetIds,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useThemePacks(): ThemePacksState {
  return useContext(Ctx);
}
